import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';
import { logtail } from '@/utils/logtail';
import headerObject from '@/types/headerObj';

const statusEndpoint = apiUrl + 'trialAccessStatus/';
const setEndpoint = apiUrl + 'setTrialAccess/';

export interface TrialAccessStatus {
  /** False when the org has no review_seat_cap — every other field is then informational. */
  Enforced: boolean;
  SeatCap: number | null;
  SeatsUsed: number;
  IsPaid: boolean;
  InTrial: boolean;
  /** What the dashboard locks on. */
  HasAccess: boolean;
  Role: string;
  Error?: string | null;
}

export interface SetTrialAccessResult {
  Success: boolean;
  Error?: string | null;
  /** Stable machine code: seat_cap_reached | not_found | invalid_input | feature_gated | internal_error */
  Code?: string | null;
  SeatsUsed?: number | null;
  SeatCap?: number | null;
}

/**
 * Both endpoints return a JSON-encoded string body (Django `to_json()`), so the
 * response is parsed twice — same as removeMemberFromOrganization.
 */
const parseBody = async (response: Response) => {
  const text = await response.json();
  return typeof text === 'string' ? JSON.parse(text) : text;
};

/**
 * Seat state for the calling user. Readable by any org member, because a
 * seatless person needs it to learn why the product went quiet.
 *
 * Fails open: on any transport error this reports unenforced/has-access, so a
 * flaky network can never lock someone out of their own dashboard.
 */
export const getTrialAccessStatus = async (
  OrgUUID: string,
  headerObj?: headerObject
): Promise<TrialAccessStatus> => {
  const unenforced: TrialAccessStatus = {
    Enforced: false,
    SeatCap: null,
    SeatsUsed: 0,
    IsPaid: false,
    InTrial: false,
    HasAccess: true,
    Role: '',
  };
  try {
    const response = await postWithAuth(
      statusEndpoint,
      { OrgUUID },
      { headers: { ...headerObj } }
    );
    if (!response.ok) return unenforced;
    return (await parseBody(response)) as TrialAccessStatus;
  } catch (error) {
    logtail.error(error + ' error in getTrialAccessStatus', { OrgUUID });
    logtail.flush();
    return unenforced;
  }
};

/**
 * Grant or revoke one person's trial seat. Admin-only server-side.
 *
 * Unlike most callers here, a non-2xx response is parsed rather than thrown
 * away: refusals (409 seat_cap_reached, 400 invalid_input, ...) carry the
 * `Code` and the user-facing `Error` copy that the UI needs to show.
 */
export const setTrialAccess = async (
  {
    OrgUUID,
    UserUUID,
    Enabled,
  }: { OrgUUID: string; UserUUID: string; Enabled: boolean },
  headerObj?: headerObject
): Promise<SetTrialAccessResult> => {
  try {
    const response = await postWithAuth(
      setEndpoint,
      { OrgUUID, UserUUID, Enabled },
      { headers: { ...headerObj } }
    );
    const body = (await parseBody(response)) as SetTrialAccessResult;
    if (body && typeof body.Success === 'boolean') return body;
    return {
      Success: false,
      Error: 'Something went sideways. Try again.',
      Code: 'internal_error',
    };
  } catch (error) {
    logtail.error(error + ' error in setTrialAccess', { OrgUUID, UserUUID });
    logtail.flush();
    return {
      Success: false,
      Error: 'Something went sideways. Try again.',
      Code: 'internal_error',
    };
  }
};
